import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Collapse,
  IconButton,
  Chip,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { getChartColors, formatCurrency } from '../utils/chartConfig';
import { calculateCFOAnalysis } from '../services/paymentCalculations';

/**
 * CFOAnalysis - Advanced Financial Analysis for Payment Comparison
 *
 * Shows CFO-level metrics:
 * - NPV (Net Present Value) advantage of deferring payment
 * - Capital preservation / working capital impact
 * - CapEx vs OpEx accounting treatment comparison
 *
 * @param {number} treatmentCost - Total treatment cost
 * @param {number} annualSavings - Annual energy savings
 * @param {number} initialDiscountRate - Default discount rate from company config
 * @param {string} paymentMode - Current payment mode ('upfront' or 'threeYear')
 */
export default function CFOAnalysis({
  treatmentCost,
  annualSavings,
  initialDiscountRate = 8,
  paymentMode,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const colors = getChartColors(theme.palette.mode);
  const [discountRate, setDiscountRate] = useState(initialDiscountRate);
  const [expanded, setExpanded] = useState(true);

  // Calculate CFO metrics
  const analysis = useMemo(() => {
    return calculateCFOAnalysis(treatmentCost, annualSavings, discountRate / 100);
  }, [treatmentCost, annualSavings, discountRate]);

  // Only show when in 3-year mode
  if (paymentMode !== 'threeYear') {
    return null;
  }

  return (
    <Box sx={{ my: 4 }}>
      {/* Section Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.01em',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            CFO Financial Analysis
            <Chip
              label="3-Year Advantage"
              size="small"
              sx={{
                ml: 1,
                background: 'rgba(78, 205, 196, 0.15)',
                color: isDark ? colors.teal : 'text.primary',
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Time value of money, capital preservation, and accounting treatment
          </Typography>
        </Box>
        <IconButton onClick={() => setExpanded(!expanded)} size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box
          sx={{
            p: 3,
            bgcolor: 'background.paper',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: 'linear-gradient(90deg, #4ECDC4, #A0CE4E)',
              borderRadius: '8px 8px 0 0',
            },
          }}
        >
          {/* Three Column Layout */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            {/* NPV Analysis */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                background: 'rgba(78, 205, 196, 0.08)',
                border: '1px solid rgba(78, 205, 196, 0.2)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrendingUpIcon sx={{ color: isDark ? colors.teal : 'text.primary', fontSize: '1.25rem' }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: isDark ? colors.teal : 'text.primary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '0.7rem',
                  }}
                >
                  NPV Advantage
                </Typography>
              </Box>

              {/* Discount Rate Input */}
              <TextField
                label="Discount Rate"
                type="number"
                size="small"
                value={discountRate}
                onChange={(e) => setDiscountRate(Math.max(0, Math.min(30, parseFloat(e.target.value) || 0)))}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                sx={{
                  mb: 2,
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(0,0,0,0.2)',
                  },
                }}
              />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">
                    Upfront NPV:
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: '"JetBrains Mono", monospace', color: colors.orange }}
                  >
                    {formatCurrency(analysis.npv.upfront)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">
                    3-Year NPV:
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: '"JetBrains Mono", monospace', color: isDark ? colors.teal : '#1a1a1a' }}
                  >
                    {formatCurrency(analysis.npv.threeYear)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                    pt: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Time Value Savings:
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontWeight: 700,
                      color: isDark ? colors.primary : 'text.primary',
                      fontSize: '0.9rem',
                    }}
                  >
                    {formatCurrency(analysis.npv.advantage)}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: isDark ? colors.primary : 'text.primary',
                    fontWeight: 600,
                    textAlign: 'center',
                    background: 'rgba(160, 206, 78, 0.1)',
                    borderRadius: 1,
                    py: 0.5,
                  }}
                >
                  {analysis.npv.advantagePct.toFixed(1)}% effective savings
                </Typography>
              </Box>
            </Box>

            {/* Capital Preservation */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                background: 'rgba(99, 179, 237, 0.08)',
                border: '1px solid rgba(99, 179, 237, 0.2)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AccountBalanceIcon sx={{ color: isDark ? colors.blue : 'text.primary', fontSize: '1.25rem' }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: isDark ? colors.blue : 'text.primary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '0.7rem',
                  }}
                >
                  Capital Preservation
                </Typography>
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Working capital retained vs upfront payment
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Year 1:
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontWeight: 600,
                      color: isDark ? colors.primary : 'text.primary',
                    }}
                  >
                    +{formatCurrency(analysis.capitalPreservation.year1Retained)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Year 2:
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontWeight: 600,
                      color: isDark ? colors.teal : 'text.primary',
                    }}
                  >
                    +{formatCurrency(analysis.capitalPreservation.year2Retained)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Year 3:
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      color: colors.grey,
                    }}
                  >
                    $0
                  </Typography>
                </Box>
                <Box
                  sx={{
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                    pt: 1.5,
                    mt: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDark ? colors.blue : 'text.primary',
                      fontWeight: 600,
                      display: 'block',
                      textAlign: 'center',
                      background: 'rgba(99, 179, 237, 0.1)',
                      borderRadius: 1,
                      py: 0.5,
                    }}
                  >
                    Avg: {formatCurrency(analysis.capitalPreservation.averageCapitalAvailable)}/yr
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Accounting Treatment */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                background: 'rgba(160, 206, 78, 0.08)',
                border: '1px solid rgba(160, 206, 78, 0.2)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ReceiptLongIcon sx={{ color: isDark ? colors.primary : 'text.primary', fontSize: '1.25rem' }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: isDark ? colors.primary : 'text.primary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '0.7rem',
                  }}
                >
                  Accounting Treatment
                </Typography>
              </Box>

              {/* Comparison Table */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.orange,
                      fontWeight: 600,
                      display: 'block',
                      mb: 0.5,
                    }}
                  >
                    Upfront: CapEx
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Capitalized • Depreciated over time • CapEx approval required
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDark ? colors.primary : 'text.primary',
                      fontWeight: 600,
                      display: 'block',
                      mb: 0.5,
                    }}
                  >
                    3-Year: OpEx
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Operating expense • Immediate tax deduction • Simpler approval
                  </Typography>
                </Box>

                <Box
                  sx={{
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                    pt: 1.5,
                  }}
                >
                  <Chip
                    label="OpEx = Easier Budget Approval"
                    size="small"
                    sx={{
                      width: '100%',
                      background: 'rgba(160, 206, 78, 0.15)',
                      color: isDark ? colors.primary : 'text.primary',
                      fontWeight: 600,
                      fontSize: '0.65rem',
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Summary Message */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 1,
              background: 'rgba(160, 206, 78, 0.08)',
              border: '1px solid rgba(160, 206, 78, 0.2)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
            }}
          >
            <Typography sx={{ fontSize: '1.25rem' }}>💡</Typography>
            <Box>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}>
                3-Year Payment Financial Benefits
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {analysis.summary.npvMessage}. {analysis.summary.capitalMessage}. {analysis.summary.accountingMessage}.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
